import moment from "moment";
import { createPendingCall, listCalls } from "../components/calls/service";
import { unique } from "../utils";
import { createTemporaryId } from "../utils/model";
import { BasicFieldReference } from "./BasicField";
import { CallFilters, CallConnectionState } from "./CallFilters";
import { LeadListMode } from "./LeadListMode";
import Sort from "./Sort";
import { UnroutedContactFlowStage } from "./UnroutedContactFlow";
import { CallEvaluation } from "./CallEvalutation";
import Call from "./Call";
import { text } from "../utils/i18n";
import { Organization } from "./Organization";
import { findValidPhoneNumber } from "sweet-validator-utils";
import { ExecuteCallActionResult } from "../components/telephony/CallActionContextProvider/service";
import { Country } from "./Country";
import { CallEvaluationOptions } from "./CallSessionTypes";
import Offer from "./Offer";

export enum CallTransferDirection {
  /**
   * The call was transferred to this user.
   */
  To = 1,

  /**
   * The call was transferred from this user.
   */
  From = 2
}

export enum TelephonyStatus {
  Idle = "idle",

  /**
   * User is reviewing contact info before making the call.
   * Used in sequential mode.
   */
  PreReviewing = "preReviewing",
  Ringing = "ringing",
  Connected = "connected",
}

export enum CallSessionReferrer {
  LeadListContactList = 1
}

/**
 * A call session is created on contact reserve or when we dial a number and is removed when the call is evaluated.
 * For predictive dialing, a call session is not created until a contact is connecting.
 */
export class CallSession {
  readonly sessionId = createTemporaryId();
  readonly callCreateError?: any;
  readonly userPhoneNumber?: string;
  /**
   * List of the current user's call IDs reported by EasyTelecom.
   * In most cases there is at most 1 ID, but due to a bug (likely) there can be several.
   * 
   * The bug might be our fault or it might not, but the most logical thing right now is that
   * if we have several IDs then we should try each to resolve a SaleSys call ID.
   * 
   * See https://trello.com/c/TU08jySX
   */
  readonly easyTelecomUserCallIds: string[] = [];
  readonly callId?: string;
  readonly call?: Call;
  pendingCallId?: string;
  /**
   * For inbound or transferred calls, whether the call has been answered.
   */
  readonly isAnswered?: boolean;
  readonly connectedAt?: Date;
  readonly connectingAt?: Date;
  readonly parkedAt?: Date; //  Sätts av vyer.
  readonly contactId?: string;
  /**
   * Whether the contact was created between the call.
   */
  readonly isNewContact: boolean = false;
  /**
   * Optional ID of another session which 'interrupted' this session.
   * For example, given an active session A, answering an inbound session B interrupts session A. 
   */
  readonly interruptedBySessionId?: string;
  readonly sentDigits: string = "";
  readonly isCohear: boolean = false;
  readonly isInbound: boolean = false;
  /**
   * If set, then the call was transferred.
   */
  readonly transferDirection?: CallTransferDirection = null;

  /**
   * Information about the dialed contact.
   * Does not depend on GetContactData right. See issue #709 
   */
  readonly contactInfo?: BasicFieldReference[];
  readonly disconnectedAt?: Date;

  /**
   * Phone number to ring, or for inbound calls, the phone number which made the call.
   * If set, then assume we are trying to make the call right now.
   * 
   * For pre-review, this should not be set until the user clicks 'ring'.
   */
  readonly contactPhoneNumber?: string;
  /**
   * If the user changes the field which matches the contact phone number,
   * then this will be the updated value.
   */
  readonly updatedContactPhoneNumber?: string;
  /**
   * List of phone numbers returned when reserving the contact.
   */
  readonly contactPhoneNumbers: string[] = [];
  readonly leadListId?: string;
  readonly shuffleLeadListId?: string;

  /**
   * The current lead list mode when this session was created.
   */
  readonly leadListMode: LeadListMode | null = null;
  readonly unroutedContactFlowId?: string;
  readonly unroutedContactFlowStage?: UnroutedContactFlowStage;
  readonly evaluation = new CallEvaluation();
  /**
   * If true, then user clicked to show evaluation.
   */
  readonly isEvaluationVisible?: true;
  readonly evaluationOptions: CallEvaluationOptions = { callNextContact: true };

  /**
   * ID of a call action currently being executed on this call.
   */
  readonly executingActionId?: string;

  /**
   * ID of a call action serie currently being executed on this call.
   */
  readonly executingSerieId?: string;

  /**
   * Object of call actions which have been executed.
   */
  readonly actionExecutions: ById<ExecuteCallActionResult[]> = {};

  /**
   * List of IDs of call action series executed on this call.
   */
  readonly executedSerieIds: string[] = [];

  /**
   * Whether selecting callback time in call evaluation section.
   */
  readonly isSelectingCallbackTime: boolean = false;

  /**
   * Input of a pending comment.
   */
  readonly commentInput: string = "";

  /**
   * Whether to automatically refresh contact info.
   */
  readonly autoRefreshContactInfo: boolean = false;

  readonly isRefreshingContactInfo: boolean = false;
  /**
   * Optional, used for resolving phone number.
   */
  readonly country?: Country;

  readonly status: TelephonyStatus;

  /**
   * Associated offer.
   */
  readonly offer?: Offer;

  constructor(deriveFrom?: Partial<CallSession>) {
    if (!deriveFrom) {
      return;
    }

    Object.assign(this, deriveFrom);

    this.status = this.getStatus();

    this.easyTelecomUserCallIds = unique(this.easyTelecomUserCallIds.filter(id => id));
  }

  get isActive() {
    if (this.disconnectedAt) {
      return false;
    }

    return Boolean(this.connectedAt || this.connectingAt);
  }

  get isParked() {
    return this.isActive && Boolean(this.parkedAt);
  }

  get hasError() {
    if (this.callCreateError) {
      return true;
    }

    return false;
  }

  get isEvaluationPending() {
    if (!this.disconnectedAt || this.isCohear || this.transferDirection === CallTransferDirection.From) {
      return false;
    }

    if (this.leadListMode === LeadListMode.Predictive && !this.connectedAt) {
      //  Do not create or evaluate a system-created, unrouted call.
      return false;
    }

    return true;
  }

  get isTransferred() {
    return Boolean(this.transferDirection);
  }

  get isConnected() {
    return Boolean(this.connectedAt && !this.disconnectedAt);
  }

  get isUnrouted() {
    return Boolean(this.disconnectedAt && !this.connectedAt);
  }

  get isEvaluationRequired() {
    return !this.interruptedBySessionId;
  }

  get wasInterrupted() {
    return Boolean(this.interruptedBySessionId);
  }

  /**
   * A human-readable description of this session.
   */
  get description() {
    if (!this.connectedAt && !this.connectingAt) {
      return null;
    }

    return moment(this.connectedAt || this.connectingAt).format('HH:mm');
  }

  /**
   * A human-readable description of this session.
   */
  get longDescription() {
    if (!this.connectedAt && !this.connectingAt) {
      return null;
    }

    if (this.contactPhoneNumber && this.connectedAt) {
      return text`Ditt samtal med ${this.contactPhoneNumber} som kopplades vid ${moment(this.connectedAt).format('HH:mm')}`;
    } else if (this.contactPhoneNumber) {
      return text`Ditt obesvarade samtal till ${this.contactPhoneNumber} som ringdes vid ${moment(this.connectingAt).format('HH:mm')}`;
    } else if (this.connectedAt) {
      return text`Ditt samtal som kopplades vid ${moment(this.connectedAt).format('HH:mm')}`;
    } else {
      return text`Ditt obesvarade samtal som ringdes vid ${moment(this.connectingAt).format('HH:mm')}`;
    }
  }

  async getCall(): Promise<Call | null> {
    if (!this.easyTelecomUserCallIds.length) {
      throw new Error("`easyTelecomUserCallId` must be set to resolve call ID");
    }

    const [call] = await listCalls(new CallFilters({
      count: 1,
      easyTelecomUserCallIds: this.easyTelecomUserCallIds,
      connectionState: CallConnectionState.Any,
      // If there somehow are multiple calls with the same ID, select the latest one.
      sort: new Sort({ asc: false, field: 'date' })
    }));

    if (!call) {
      throw new Error("Call does not exist");
    }

    return call;
  }
  
  async createPendingCallId(): Promise<string | null> {
    if (this.connectingAt) {
      //  Samtalet är påbörjat, använd det riktiga ID:t istället.
      return null;
    }

    if (this.pendingCallId) {
      return this.pendingCallId;
    }

    const pendingCall = await createPendingCall(new Call({
      contactId: this.contactId,
      recipient: this.contactPhoneNumber ?? this.contactPhoneNumbers[0],
      dialGroupId: this.leadListId,
      dialGroupMode: this.leadListMode,
    }));

    this.pendingCallId = pendingCall.id;
    
    return this.pendingCallId;
  }

  async resolveCallId(): Promise<string | null> {
    if (this.callId) {
      //  For EasyTelecom calls, inbound and transferred calls has a callId set.
      return this.callId;
    }

    const call = await this.getCall();
    return call.id;
  }

  /**
   * Returns whether consequent calls should *not* wait for this call to be evaluated.
   * If true, then the call is evaluated in a separate widget.
   */
  get isEvaluatedInSeparateWidget() {
    return this.leadListMode === LeadListMode.Predictive || this.wasInterrupted;
  }

  private getStatus(): TelephonyStatus {
    if (this.isEvaluationPending) {
      return TelephonyStatus.Idle;
    }

    if (this.isConnected) {
      return TelephonyStatus.Connected;
    }

    if (this.connectingAt && !this.isInbound && !this.isTransferred && !this.isCohear) {
      return TelephonyStatus.Ringing;
    }

    if (this.leadListMode === LeadListMode.Sequential || this.leadListMode === LeadListMode.Single) {
      return TelephonyStatus.PreReviewing;
    }

    return TelephonyStatus.Idle;
  }

  /**
   * Returns a list of all phone numbers associated with this session.
   */
  listPhoneNumbers(organization: Organization) {
    const phoneNumbers: string[] = [];

    if (this.contactPhoneNumber) {
      phoneNumbers.push(this.contactPhoneNumber);
    }

    if (this.contactPhoneNumbers?.length) {
      this.contactPhoneNumbers.forEach(phoneNumber => phoneNumbers.push(phoneNumber));
    }

    if (this.contactInfo?.length) {
      findValidPhoneNumber(this.contactInfo.map(({ value }) => value), {
        countries: organization.countries,
        priorityCountry: organization.mainCountry
      }).forEach(phoneNumber => phoneNumbers.push(phoneNumber));
    }

    return unique(phoneNumbers);
  }
}